import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, errText } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowRight, Plus, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

const StatusBadge = ({ p }) => {
  if (p.disabled) return <Badge variant="destructive" className="text-[10px]">Suspended</Badge>;
  const map = { active: 'bg-primary/10 text-primary border-primary/30', trial: '', past_due: 'bg-amber-500/10 text-amber-600 border-amber-500/30', cancelled: '' };
  return <Badge variant="outline" className={`text-[10px] capitalize ${map[p.subscription_status] || ''}`}>{p.subscription_status || 'trial'}</Badge>;
};

export default function OwnerPractices() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [np, setNp] = useState({ name: '', address: '', billing_email: '', practice_number: '' });

  const load = () => api.get('/owner/practices').then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const createPractice = async () => {
    if (np.name.trim().length < 2) { toast.error('Practice name is required'); return; }
    setCreating(true);
    try {
      const r = await api.post('/owner/practices', np);
      toast.success(`${r.data.name} created — add the doctor and staff from the practice page`);
      setOpen(false);
      setNp({ name: '', address: '', billing_email: '', practice_number: '' });
      load();
      window.location.href = `/owner/practices/${r.data.id}`;
    } catch (e) {
      toast.error(errText(e, 'Could not create practice'));
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (p, on) => {
    try {
      await api.patch(`/owner/practices/${p.id}`, { disabled: !on });
      toast.success(!on ? `${p.name} suspended — staff access blocked` : `${p.name} re-activated`);
      load();
    } catch (e) {
      toast.error(errText(e, 'Could not update practice'));
    }
  };

  if (!items) return <div className="p-4 lg:p-6 max-w-7xl mx-auto space-y-3"><Skeleton className="h-8 w-56" /><Skeleton className="h-64" /></div>;

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto space-y-4" data-testid="owner-practices-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Practices</h1>
          <p className="text-sm text-muted-foreground">Every doctor customer on the platform. Toggle a practice off to suspend all staff access instantly.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="owner-new-practice-btn"><Plus className="h-4 w-4 mr-1" /> New practice</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create a new practice</DialogTitle>
              <DialogDescription>Sets up a Plan C workspace by default — we collect nothing from patients (the practice keeps any patient fee); set or waive the platform fee on the practice's page afterward. You'll add the doctor and staff on the next screen — each gets a secure password-setup link.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="np-name">Practice name *</Label>
                <Input id="np-name" value={np.name} onChange={(e) => setNp((s) => ({ ...s, name: e.target.value }))} placeholder="Lakeview Sports Medicine" data-testid="owner-np-name" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="np-address">Address</Label>
                <Input id="np-address" value={np.address} onChange={(e) => setNp((s) => ({ ...s, address: e.target.value }))} placeholder="200 Main St, Suite 4, Springfield IL" data-testid="owner-np-address" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="np-email">Billing email</Label>
                  <Input id="np-email" type="email" value={np.billing_email} onChange={(e) => setNp((s) => ({ ...s, billing_email: e.target.value }))} placeholder="billing@practice.com" data-testid="owner-np-email" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="np-number">Practice # (NPI)</Label>
                  <Input id="np-number" value={np.practice_number} onChange={(e) => setNp((s) => ({ ...s, practice_number: e.target.value }))} placeholder="NPI 1234567890" data-testid="owner-np-number" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} disabled={creating}>Cancel</Button>
              <Button onClick={createPractice} disabled={creating} data-testid="owner-np-create">
                {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />} Create practice
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {items.map((p) => (
          <Card key={p.id} data-testid={`owner-practice-card-${p.id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium truncate">{p.name}</p>
                  <div className="flex gap-1.5 mt-1"><StatusBadge p={p} />{p.is_demo && <Badge variant="secondary" className="text-[10px]">Demo</Badge>}</div>
                </div>
                <Switch checked={!p.disabled} onCheckedChange={(v) => toggle(p, v)} data-testid={`owner-practice-toggle-${p.id}`} />
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                <span>{p.users_active} users</span>
                <span>{fmt(p.monthly_total)}/mo</span>
                <span>{p.submissions_total} subs</span>
                <span className="col-span-3">AI cost (estimate): {fmt(p.ai_cost_this_month)} of {fmt(p.ai_allowance_this_month)} fair use</span>
              </div>
              <Button asChild variant="outline" size="sm" className="w-full"><Link to={`/owner/practices/${p.id}`}>Manage <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link></Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Desktop table */}
      <Card className="hidden md:block">
        <Table data-testid="owner-practices-table">
          <TableHeader>
            <TableRow>
              <TableHead>Practice</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Users</TableHead>
              <TableHead>Seats (D·A·S)</TableHead>
              <TableHead className="text-right">Monthly</TableHead>
              <TableHead>Billing</TableHead>
              <TableHead className="text-right">Patient fees</TableHead>
              <TableHead className="text-right">Submissions</TableHead>
              {/* ADR 0002: our cost, beside its fair-use line. Never invoiced. */}
              <TableHead className="text-right" title="What the AI cost us this month, beside the fair-use line. Included in the subscription — never invoiced.">AI cost (estimate)</TableHead>
              <TableHead>Active</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((p) => (
              <TableRow key={p.id} data-testid={`owner-practice-row-${p.id}`}>
                <TableCell className="font-medium">
                  {p.name}
                  {p.is_demo && <Badge variant="secondary" className="ml-2 text-[10px]">Demo</Badge>}
                </TableCell>
                <TableCell><StatusBadge p={p} /></TableCell>
                <TableCell className="text-right tabular-nums">{p.users_active}</TableCell>
                <TableCell className="text-xs text-muted-foreground tabular-nums">{p.seat_usage.doctor} · {p.seat_usage.admin} · {p.seat_usage.staff}</TableCell>
                <TableCell className="text-right tabular-nums">{fmt(p.monthly_total)}</TableCell>
                {/* ADR 0001: mode 3 practices bill their own patients and owe us
                    a fixed share per document, invoiced at month end. ADR 0004:
                    a Plan C site has no per-document fee at all — we take
                    nothing from its patients and price its organisation's month,
                    so there is no /doc figure to show and inventing one would
                    read as a charge. */}
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap" data-testid={`owner-practice-billing-${p.id}`}>
                  {p.pricing_model === 'planc'
                    ? <>Plan C · no patient fee<br /><span className="text-muted-foreground/70">{p.documents_this_month} submissions this month</span></>
                    : p.billing_mode === 3
                    ? <>Practice collects · {fmt(p.platform_share)}/doc<br /><span className="text-muted-foreground/70">{p.documents_this_month} this month · {fmt(p.accrued_this_month)} accruing</span></>
                    : <>We collect · {fmt(p.platform_share)}/doc</>}
                </TableCell>
                <TableCell className="text-right tabular-nums">{fmt(p.patient_fees_collected)}</TableCell>
                <TableCell className="text-right tabular-nums">{p.submissions_total}</TableCell>
                <TableCell
                  className={`text-right tabular-nums text-xs whitespace-nowrap${(p.ai_cost_this_month ?? 0) > (p.ai_allowance_this_month ?? 0) ? ' text-amber-600' : ''}`}
                  data-testid={`owner-practice-ai-${p.id}`}
                >
                  {fmt(p.ai_cost_this_month)}<br /><span className="text-muted-foreground/70">of {fmt(p.ai_allowance_this_month)}</span>
                </TableCell>
                <TableCell><Switch checked={!p.disabled} onCheckedChange={(v) => toggle(p, v)} data-testid={`owner-practice-toggle-${p.id}`} /></TableCell>
                <TableCell>
                  <Button asChild variant="ghost" size="sm" data-testid={`owner-practice-open-${p.id}`}><Link to={`/owner/practices/${p.id}`}>Manage</Link></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
